import { Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import {
  ListState,
  ListType,
  ListViewMode,
  recentPurchaseLoaded,
  SelectedListProductNumbers,
  selectRecentPurchaseState,
  selectSelectedListProductNumbers,
} from '@usf/ngrx-list';
import { EasyPricingService, isEasyPricingLoaded } from '@usf/ngrx-pricing';
import { getLoadingStateOfProducts, LoadingState } from '@usf/ngrx-product';
import { combineLatest, filter, map, Observable, of, switchMap } from 'rxjs';
import { mslProductLoadCheck } from '../pages/master-list/selectors/master-list.selectors';
import {
  selectCombinedListAndOgHeaders,
  selectListStatesAreLoaded,
} from '../shared/list-detail-management/selectors/list-detail-management-view-model.selector';

@Injectable({
  providedIn: 'root',
})
export class ClientListViewService {
  isEasyPricingLoadedForSelectedList$: Observable<boolean> = this.store
    .select(selectSelectedListProductNumbers)
    .pipe(
      switchMap((selectedListProductNumbers: SelectedListProductNumbers) =>
        this.store.select(
          isEasyPricingLoaded(selectedListProductNumbers.productNumbers),
        ),
      ),
    );

  isSelectedListLoaded$: Observable<boolean> = this.store
    .select(selectSelectedListProductNumbers)
    .pipe(
      switchMap((selectedListProductNumbers: SelectedListProductNumbers) => {
        const listType = selectedListProductNumbers.selectedListType;
        const viewMode = selectedListProductNumbers.selectedListViewMode;
        const productNumbers = selectedListProductNumbers.productNumbers;
        if (listType === ListType.masterList) {
          return of(false);
        }

        return listType === ListType.recentPurchase
          ? this.getRecentlyPurchasedLoaded(viewMode, productNumbers)
          : this.getNormalOrOrderGuideLoaded(selectedListProductNumbers);
      }),
    );

  constructor(private readonly store: Store) {}

  private getRecentlyPurchasedLoaded(
    viewMode: ListViewMode,
    productNumbers: number[],
  ): Observable<boolean> {
    return combineLatest([
      this.store.select(recentPurchaseLoaded()),
      this.store.select(selectRecentPurchaseState),
    ]).pipe(
      filter(
        ([loaded, rpState]) =>
          loaded &&
          ((productNumbers && productNumbers.length > 0) ||
            rpState.ids.length === 0),
      ),
      switchMap(([loaded]) =>
        this.checkListLoaded(of(loaded), viewMode, productNumbers),
      ),
    );
  }

  private getNormalOrOrderGuideLoaded(
    selectedListProductNumbers: SelectedListProductNumbers,
  ): Observable<boolean> {
    return combineLatest([
      this.store.select(selectListStatesAreLoaded()),
      this.store
        .select(selectCombinedListAndOgHeaders())
        .pipe(
          map(
            (dict: Dictionary<ListState>) =>
              dict[
                `${selectedListProductNumbers.selectedListType}-${selectedListProductNumbers.selectedListId}`
              ],
          ),
        ),
    ]).pipe(
      filter(
        ([loaded, list]) =>
          loaded &&
          ((selectedListProductNumbers.productNumbers &&
            selectedListProductNumbers.productNumbers.length > 0) ||
            (!!list && list.listItemCount === 0)),
      ),
      switchMap(([loaded]) =>
        this.checkListLoaded(
          of(loaded),
          selectedListProductNumbers.selectedListViewMode,
          selectedListProductNumbers.productNumbers,
        ),
      ),
    );
  }

  private checkListLoaded(
    listLoaded$: Observable<boolean>,
    viewMode: ListViewMode,
    productNumbers: number[],
  ): Observable<boolean> {
    return combineLatest([
      listLoaded$,
      this.store.select(mslProductLoadCheck),
      this.store.select(getLoadingStateOfProducts(productNumbers)),
      viewMode === ListViewMode.pricing
        ? this.isEasyPricingLoadedForSelectedList$
        : of(true),
    ]).pipe(
      map(
        ([listLoaded, mslLoaded, productLoading, easyPricingLoaded]) =>
          listLoaded &&
          mslLoaded &&
          productLoading === LoadingState.loaded &&
          easyPricingLoaded,
      ),
    );
  }
}
